import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";

import Img from "../../../assets/images/stakeholder.jpg";

export default function StakeholderEngagement({ isOpen, onClose }) {
  const overlayRef = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && (videoOpen ? setVideoOpen(false) : onClose?.());
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose, videoOpen]);

  if (!isOpen) return null;

  const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Stakeholder engagement", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };

  // ID estratto dal link vimeo https://vimeo.com/1040475092?share=copy
  const VIMEO_ID = "1040475092";
  const VIMEO_SRC = `https://player.vimeo.com/video/${VIMEO_ID}?autoplay=1&title=0&byline=0&portrait=0`;

  return createPortal(
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[2400] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
        onMouseDown={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
        role="dialog"
        aria-modal="true"
        aria-label="Stakeholder Engagement"
      >
        <div
          className="relative w-full md:w-[1280px] bg-white max-h-[90vh] overflow-y-auto overscroll-contain"
          style={{ background: "#E7E3DF", WebkitOverflowScrolling: "touch" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
            <button
              onClick={() => onShare("StakeholderEngagement")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
              title="Condividi"
              aria-label="Condividi"
            >
              <img src={shareIcon} alt="" className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
              title="Chiudi"
              aria-label="Chiudi"
            >
              <img src={closeIcon} alt="Chiudi" className="h-4 w-4" />
            </button>
          </div>

          {/* Contenuto */}
          <div className="p-8">
            <h2 className="text-[#67140F] text-[30px] md:text-[40px] font-bold">
              Stakeholder Engagement
            </h2>

            <div className="mt-8 flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Immagine */}
              <img
                src={Img}
                alt=""
                className="w-full max-w-[420px] h-auto"
              />

              <div>
                {/* Testo */}
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em]">
                  A true sustainability integrated into the business is possible if people get used to 'thinking about sustainability.' This was the driver that led to the <b>Impact Innovators Board</b>, 
                  initiative, an internal international competition that invited Reale Group employees to present highly innovative <b>projects on some sustainability topics</b> tailored to the Group's reality.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em]">
                  Many responded to the call, and at the end of the selection process, a jury selected <b>13 finalist projects from Italy, Spain, Greece, and Chile. </b> 
                  The finalists then formed a real Board, in a role-playing game at the end of which <b>3 winning projects</b> emerged.
                  Phase 2 of the initiative involves a feasibility check of the project for its implementation.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8">
              <button
                className="bt-secondary text-sm px-4 py-2"
                style={{ background: "#0E3761", color: "#fff" }}
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-haspopup="dialog"
                aria-controls="stakeholder-video-modal"
              >
                <img
                  src={arrowIconWhite}
                  alt="Arrow"
                  className="inline-block mr-2 w-[20px] h-[20px]"
                />
                Watch the video
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* VIDEO POPUP */}
      {videoOpen && createPortal(
        <div
          id="stakeholder-video-modal"
          className="fixed inset-0 z-[2600] bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Riproduzione video"
          onMouseDown={(e) => {
            // chiudi cliccando il backdrop
            if (e.currentTarget === e.target) setVideoOpen(false);
          }}
        >
          <div className="relative w-full max-w-[960px]">
            {/* Close */}
            <button
              onClick={() => setVideoOpen(false)}
              className="absolute -top-10 right-0 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/90 bg-white/10 border border-white/20 hover:bg-white/20 transition"
              title="Chiudi video"
              aria-label="Chiudi video"
            >
              <img src={closeIcon} alt="Chiudi" className="h-4 w-4 invert" />
            </button>

            {/* 16:9 wrapper */}
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src={VIMEO_SRC}
                title="Stakeholder Engagement - Video"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>,
    document.body
  );
}
